/**
 * The answering agent's data layer on the Ask tab (2026-09-26, Phase 5): the
 * steps of an answer, and Stop.
 *
 * Its own file, beside `chat-api.ts` rather than in it, as `mark-wrong-api.ts`
 * is: the thread's reads and writes stay where they were, and a screen that
 * stands in for them in a test still renders.
 */
import { useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import type { AnswerStep } from "@gb/contracts";
import { api, errorMessage } from "@/api/client";

/** The same as the thread's, so a question's steps re-check with it. */
const POLL_WHILE_ANSWERING_MS = 2_000;

/**
 * The steps of a thread's answer.
 *
 * **It extends the thread's key** (`chatKeys.thread` in `chat-api.ts`), so
 * invalidating a thread reads its steps back too. Written out rather than
 * imported, for the reason at the top of this file; `answer-steps-api.test.tsx`
 * holds the two together.
 */
export function answerStepsKey(projectId: string, threadId: string) {
  return ["project", projectId, "chat", "thread", threadId, "steps"] as const;
}

/**
 * The steps the Brain took to answer, in order.
 *
 * **Polled while the question has no answer**, like the thread: the steps are
 * written as each one finishes, so a screen reloaded mid-answer fills its list
 * in while the server carries on. Once answered, read once.
 *
 * **Read once more when the answer arrives** (2026-09-27). The poll stops
 * then, and its last read can be up to 2 seconds older than the answer: from
 * before the review's row and the last turn's steps were written. Nothing
 * else reads the steps again, because the thread's poll refetches the thread
 * only and does not invalidate the keys that extend it. So a list polled
 * while the answer ran showed "Checking the answer…" under the finished
 * answer until the window next took focus.
 *
 * A route that answers anything but 200 leaves the list empty rather than
 * failing the screen: the steps explain an answer, and the answer stands
 * without them.
 */
export function useAnswerSteps(
  projectId: string | undefined,
  threadId: string | undefined,
  opts: { enabled: boolean; answered: boolean },
) {
  const enabled = Boolean(projectId && threadId) && opts.enabled;
  const query = useQuery({
    queryKey: answerStepsKey(projectId ?? "", threadId ?? ""),
    enabled,
    refetchInterval: opts.answered ? false : POLL_WHILE_ANSWERING_MS,
    queryFn: async (): Promise<AnswerStep[]> => {
      const res = await api.chat.listSteps({
        params: { id: projectId!, threadId: threadId! },
      });
      if (res.status !== 200) throw new Error(errorMessage(res));
      return res.body.steps;
    },
  });

  // The thread whose steps were polled while it had no answer.
  const polledFor = useRef<string | null>(null);
  const { refetch } = query;
  useEffect(() => {
    if (!enabled || !threadId) return;
    if (!opts.answered) polledFor.current = threadId;
    else if (polledFor.current === threadId) {
      polledFor.current = null;
      void refetch();
    }
  }, [enabled, opts.answered, threadId, refetch]);

  return query;
}

/**
 * Stop the answer running on this thread (`product/0005` §2.6).
 *
 * Stored on the question, not sent down the stream: the answer runs on when
 * the connection closes, and the request can reach a server task that is not
 * running it. It never throws. The stream's own Stop ignores the outcome:
 * closing the stream still stops an answer on the path that stops at a closed
 * connection, and the stored thread says what happened either way.
 *
 * **The outcome, for a Stop with no stream** (2026-09-27): `stored`,
 * `answered` (nothing was left to stop), or `failed` (the stop was not
 * stored). With no stream to close, a failed stop stops nothing, so the
 * screen must say so.
 */
export async function requestCancel(
  projectId: string,
  threadId: string,
): Promise<"stored" | "answered" | "failed"> {
  try {
    const res = await api.chat.cancelAsk({ params: { id: projectId, threadId } });
    if (res.status !== 200) return "failed";
    return res.body.requested ? "stored" : "answered";
  } catch {
    return "failed";
  }
}
